import * as React from 'react';
import { Header, Loader, Table, Modal } from 'ui';
import { i18n } from 'i18n';
import { prefixClassName } from 'framework/components/ui/_conf';
import { AxeResults, Result, NodeResult, CheckResult } from 'axe-core';
import { toSentenceCase } from '../../../../utils/string';

const clx = prefixClassName('');

interface AccessibilityResultsModalProps {
  results: AxeResults | null;
  modalName: string;
  setAccessibilityResults: (results: AxeResults | null) => void;
}

export const NodeRow = ({ node }: { node: NodeResult }) => {
  return <Table.Row>
    <Table.Column width="50%">{node.html}</Table.Column>
    <Table.Column>
      <ul style={{ margin: 0, padding: 0, lineHeight: 1.5 }}>
        {
          [...node.all, ...node.any, ...node.none].map(
            (messages: CheckResult, index) => <li key={`${messages.message}-${index}`}>{messages.message}</li>,
          )
        }
      </ul>
    </Table.Column>
  </Table.Row>;
}

export const NodesTable = ({ nodes }: { nodes: NodeResult[] }) => {
  return <Table
    data={nodes}
    columnMetadata={[
      `${i18n.t('components.accessibilityModal.htmlTag')}`,
      `${i18n.t('components.accessibilityModal.issues')}`,
    ]}
    cellRenderer={(_t, node: NodeResult) => <NodeRow node={node} />}
  />;
}

export const GridRow = ({ result, index }: { result: Result, index: number }) => {
  return (
    <Table.ExpandableRow
      Content={() => <NodesTable nodes={result.nodes} />}
      colSpan={columnMetaData.length}
      index={index}
    >
      <Table.Column>{toSentenceCase(result.impact)}</Table.Column>
      <Table.Column>{result.description}</Table.Column>
      <Table.Column>{result.tags?.length > 1 ? result.tags[1] : result.tags?.[0] ?? ''}</Table.Column>
      <Table.ColumnLink to={result.helpUrl} caption={i18n.t('components.accessibilityModal.documentation')} external />
    </Table.ExpandableRow>
  );
}

const columnMetaData = [
  '',
  { caption: `${i18n.t('components.accessibilityModal.impact')}`, className: `${clx}impact-td` },
  `${i18n.t('components.accessibilityModal.description')}`,
  `${i18n.t('components.accessibilityModal.tag')}`,
  `${i18n.t('components.accessibilityModal.url')}`
];

export const AccessibilityResultsModal = (props: AccessibilityResultsModalProps) => {
  const { modalName, results, setAccessibilityResults } = props;

  return <Modal
    name={modalName}
    caption={`${i18n.t('components.accessibilityModal.accessibilityReport')}`}
    onDismiss={() => {
      setAccessibilityResults(null);
    }}
    size="extraLarge"
  >
    {!results
      ? <Loader size="lg" />
      : <>
          <Header caption={`${i18n.t('components.accessibilityModal.violations')}`} headerLevel={2} />
          <Table
            columnMetadata={columnMetaData}
            data={results?.violations ?? []}
            cellRenderer={(_t, result, _, index) => <GridRow result={result} index={index} />}
          />
          <Header caption={`${i18n.t('components.accessibilityModal.incomplete')}`} headerLevel={2} />
          <Table
            columnMetadata={columnMetaData}
            data={results?.incomplete ?? []}
            cellRenderer={(_t, result, _, index) => <GridRow result={result} index={index} />}
          />
      </>
    }
  </Modal>
}